import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  Client,
  Conversation,
  Message,
  Paginator,
  Participant,
  ParticipantUpdateReason,
  User,
} from '@twilio/conversations';
import { Observable, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import { equalUserInfos, UserInfo } from 'src/app/interfaces';
import { TwilioMessagesAdapter } from '../../helpers/twilio-message.adapter';
import {
  findMessageBySid,
  getTwilioMessageAuthorUser,
  twilioMessageAttributes,
} from '../../helpers/twilio-message.helpers';
import { ChatEngineActiveChannelService } from '../../interfaces/chat-engine/chat-engine-active-channel.service';
import { ChatEngineOperations } from '../../interfaces/chat-engine/chat-engine-error.service';
import { ChatEngineLifeСycleService } from '../../interfaces/chat-engine/chat-engine-lifecycle-service';
import {
  ChatConversation,
  ChatEngineHandle,
  ChatMessage,
  ChatTraceData,
  ChatTypingIndicator,
} from '../../interfaces/chat-model.interface';
import { TwilioChannelsService } from './twilio-channels.service';
import { TwilioClientAccessService } from './twilio-client-access-service';
import { TwilioErrorService, TwilioEventHandlers } from './twilio-error.service';
import { TwilioUsersService } from './twilio-users.service';
import {
  TwilioMessageAttributes,
  TwilioMessageUpdated,
  TwilioParticipantUpdated,
  TwilioUserAttributes,
} from './twilio.interfaces';

@Injectable()
export class TwilioActiveChannelService extends ChatEngineActiveChannelService implements OnDestroy {
  public get activeChannelChanged(): Observable<ChatConversation | null> {
    return this.activeChannelChanged$.asObservable();
  }

  public get activeChannelDetailsChanged(): Observable<void> {
    return this.activeChannelDetailsChanged$.asObservable();
  }

  public get activeChannelMessagesChanged(): Observable<ChatMessage[]> {
    return this.activeChannelMessagesChanged$.asObservable();
  }

  public get messageAdded(): Observable<ChatMessage> {
    return this.messageAdded$.asObservable();
  }

  public get initialMessagesLoaded(): Observable<void> {
    return this.initialMessagesLoaded$.asObservable();
  }

  public get moreMessagesLoaded(): Observable<void> {
    return this.moreMessagesLoaded$.asObservable();
  }

  public get loadingMessages(): Observable<boolean> {
    return this.loadingMessages$.asObservable();
  }

  public get typingActivity(): Observable<ChatTypingIndicator> {
    return this.typingActivity$.asObservable();
  }

  private activeTwilioChannel: Conversation | null = null;
  private activeTwilioChannelParticipants: Participant[] | null = null;
  private messagePaginatorPromise: Promise<Paginator<Message>> | null = null;
  private loadedOriginalMessages: Message[] = [];

  private activeChatConversation: ChatConversation | null = null;
  private convertedMessages: ChatMessage[] = [];

  private activeChannelChanged$: Subject<ChatConversation | null> = new Subject();
  private activeChannelDetailsChanged$: Subject<void> = new Subject();
  private activeChannelMessagesChanged$: Subject<ChatMessage[]> = new Subject();
  private messageAdded$: Subject<ChatMessage> = new Subject();
  private initialMessagesLoaded$: Subject<void> = new Subject();
  private moreMessagesLoaded$: Subject<void> = new Subject();
  private loadingMessages$: Subject<boolean> = new Subject();
  private typingActivity$: Subject<ChatTypingIndicator> = new Subject();

  private engineReadinessSubscription$: Subscription | null = null;
  private otherSubscriptions$: Subscription[] = [];

  private chatEngineLifeCycleService = inject(ChatEngineLifeСycleService);
  private twilioClientAccessService = inject(TwilioClientAccessService);
  private twilioErrorService = inject(TwilioErrorService);
  private twilioUsersService = inject(TwilioUsersService);
  private twilioChannelsService = inject(TwilioChannelsService);
  private sessionService = inject(SessionService);

  public constructor() {
    super();
    this.subscribeToEngineReadinessChanged();
  }

  public ngOnDestroy(): void {
    if (this.engineReadinessSubscription$) {
      this.engineReadinessSubscription$.unsubscribe();
    }
    unsubscribeAll(this.otherSubscriptions$);
    subjectsComplete([
      this.activeChannelChanged$,
      this.activeChannelDetailsChanged$,
      this.activeChannelMessagesChanged$,
      this.messageAdded$,
      this.initialMessagesLoaded$,
      this.moreMessagesLoaded$,
      this.loadingMessages$,
      this.typingActivity$,
    ]);
  }

  public async activateChannel(uniqueName: string): Promise<boolean> {
    this.clearActiveChannelData();
    this.loadingMessages$.next(true);

    try {
      const twilioConversation: Conversation | null =
        await this.twilioChannelsService.tryResolvingTwilioConversation(uniqueName);
      if (twilioConversation) {
        await this.performChannelActivation(twilioConversation);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      this.loadingMessages$.next(false);
      throw error;
    }
  }

  public closeActiveChannel(): void {
    this.clearActiveChannelData();
    this.activeChannelChanged$.next(null);
  }

  public loadMoreMessages(): void {
    if (!this.activeTwilioChannel) {
      return;
    }

    const channelUniqueName: string = this.activeTwilioChannel.uniqueName;
    this.fetchActiveChannelMessages()
      .then(() => this.moreMessagesLoaded$.next())
      .catch((error: unknown) => {
        this.twilioErrorService.onOperationFailed(ChatEngineOperations.LOAD_MORE_MESSAGES, error, {
          channelUniqueName,
        } as ChatTraceData);
      });
  }

  public async currentUserTyping(): Promise<void> {
    if (this.activeTwilioChannel) {
      await this.activeTwilioChannel.typing();
    }
  }

  public editMessage(messageId: string, updatedText: string): void {
    if (!this.activeTwilioChannel) {
      return;
    }
    const messageForEdit: Message | null = findMessageBySid(this.loadedOriginalMessages, messageId);
    if (messageForEdit) {
      const channelUniqueName: string = this.activeTwilioChannel.uniqueName;
      messageForEdit.updateBody(updatedText).catch((error: unknown) => {
        this.twilioErrorService.onOperationFailed(ChatEngineOperations.EDIT_MESSAGE, error, {
          channelUniqueName,
          messageId: messageForEdit.sid,
        } as ChatTraceData);
      });
    }
  }

  public removeMessage(messageId: string): void {
    if (!this.activeTwilioChannel) {
      return;
    }
    const messageForRemove: Message | null = findMessageBySid(this.loadedOriginalMessages, messageId);
    if (messageForRemove) {
      const channelUniqueName: string = this.activeTwilioChannel.uniqueName;
      messageForRemove.remove().catch((error: unknown) => {
        this.twilioErrorService.onOperationFailed(ChatEngineOperations.REMOVE_MESSAGE, error, {
          channelUniqueName,
          messageId: messageForRemove.sid,
        } as ChatTraceData);
      });
    }
  }

  public async markAllMessagesAsConsumed(): Promise<void> {
    if (!this.activeTwilioChannel) {
      return;
    }
    const channelUniqueName: string = this.activeTwilioChannel.uniqueName;
    this.activeChatConversation = await this.twilioChannelsService.getConversationByUniqueName(channelUniqueName);
    try {
      if (this.activeChatConversation && this.activeChatConversation.lastMessage) {
        this.activeChatConversation.lastMessage.isRead = true;
        await this.activeTwilioChannel.setAllMessagesRead();
        this.twilioChannelsService.emitSortedConversationsList();
        this.emitMessagesConsumed();
      }
    } catch (error) {
      const chatTraceData: ChatTraceData = {
        channelUniqueName,
      };
      this.twilioErrorService.onOperationFailed(
        ChatEngineOperations.MARK_ALL_MESSAGES_AS_CONSUMED,
        error,
        chatTraceData,
      );
    }
  }

  private emitMessagesConsumed(): void {
    this.activeChannelDetailsChanged$.next();
  }

  private async performChannelActivation(twilioConversation: Conversation): Promise<void> {
    this.activeTwilioChannel = twilioConversation;
    this.activeTwilioChannelParticipants = await this.activeTwilioChannel.getParticipants();

    this.activeChatConversation = await this.twilioChannelsService.getConversationByUniqueName(
      twilioConversation.uniqueName,
    );
    this.activeChannelChanged$.next(this.activeChatConversation);

    this.messagePaginatorPromise = this.activeTwilioChannel.getMessages(AppSettings.CHAT_MESSAGES_CHUNK);
    await this.fetchActiveChannelMessages();
    this.initialMessagesLoaded$.next();
  }

  private subscribeToEngineReadinessChanged(): void {
    this.engineReadinessSubscription$ = this.chatEngineLifeCycleService.engineReadinessChanged.subscribe(
      (engineHandle: ChatEngineHandle) => {
        if (engineHandle.isValid()) {
          this.subscribeToUserEvents();
          this.subscribeToChannelRemoved();
          this.subscribeToMessageEventsInChannel();
          this.subscribeToParticipantEventsInChannel();
          this.subscribeToTypingEventsInChannel();
        } else {
          unsubscribeAll(this.otherSubscriptions$);
          this.clearActiveChannelData();
        }
      },
    );
  }

  private subscribeToChannelRemoved(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('conversationRemoved', (conversation: Conversation) => {
      if (this.activeTwilioChannel && this.activeTwilioChannel.uniqueName === conversation.uniqueName) {
        this.closeActiveChannel();
      }
    });
  }

  private subscribeToMessageEventsInChannel(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('messageAdded', (message: Message) => {
      if (this.activeTwilioChannel && this.activeTwilioChannel.uniqueName === message.conversation.uniqueName) {
        this.onMessageAddedInActiveChannel(message).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.ACTIVE_CHANNEL_MESSAGE_ADDED, error, {
            messageId: message.sid,
            channelUniqueName: message.conversation.uniqueName,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('messageRemoved', (message: Message) => {
      if (this.activeTwilioChannel && this.activeTwilioChannel.uniqueName === message.conversation.uniqueName) {
        this.onMessageRemovedFromActiveChannel(message);
      }
    });

    twilioClient.on('messageUpdated', (event: TwilioMessageUpdated) => {
      if (this.activeTwilioChannel && this.activeTwilioChannel.uniqueName === event.message.conversation.uniqueName) {
        this.onMessageUpdatedInActiveChannel(event).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(
            TwilioEventHandlers.ACTIVE_CHANNEL_MESSAGE_UPDATED,
            error,
            {
              messageId: event.message.sid,
              channelUniqueName: event.message.conversation.uniqueName,
            } as ChatTraceData,
          );
        });
      }
    });
  }

  private async onMessageAddedInActiveChannel(message: Message): Promise<void> {
    // Deduplication guard against network noise
    if (this.loadedOriginalMessages.some((m: Message) => m.sid === message.sid)) {
      return;
    }

    const convertedMessage: ChatMessage = await this.convertTwilioMessage(message);

    this.loadedOriginalMessages.push(message);
    this.convertedMessages = [...this.convertedMessages, convertedMessage];

    this.messageAdded$.next(convertedMessage);
    this.emitMessagesChanged();
  }

  private onMessageRemovedFromActiveChannel(message: Message): void {
    const removedMessageIndex: number = this.loadedOriginalMessages.findIndex((m: Message) => m.sid === message.sid);
    if (removedMessageIndex !== -1) {
      this.loadedOriginalMessages.splice(removedMessageIndex, 1);
      this.convertedMessages.splice(removedMessageIndex, 1);

      this.convertedMessages = [...this.convertedMessages];
      this.emitMessagesChanged();
    }
  }

  private async onMessageUpdatedInActiveChannel(event: TwilioMessageUpdated): Promise<void> {
    const updatedMessageIndex: number = this.loadedOriginalMessages.findIndex(
      (m: Message) => m.sid === event.message.sid,
    );
    if (updatedMessageIndex !== -1) {
      const convertedMessage: ChatMessage = await this.convertTwilioMessage(event.message);

      this.loadedOriginalMessages[updatedMessageIndex] = event.message;
      this.convertedMessages[updatedMessageIndex] = convertedMessage;

      this.convertedMessages = [...this.convertedMessages];
      this.emitMessagesChanged();
    }
  }

  private subscribeToParticipantEventsInChannel(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('participantUpdated', (event: TwilioParticipantUpdated) => {
      if (
        this.activeTwilioChannel &&
        this.activeTwilioChannel.uniqueName === event.participant.conversation.uniqueName &&
        event.participant.identity !== this.sessionService.userId &&
        event.updateReasons.some((r: ParticipantUpdateReason) => r === 'lastReadMessageIndex')
      ) {
        this.actualizeMessagesReadStatus(event.participant.lastReadMessageIndex).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(
            TwilioEventHandlers.ACTIVE_CHANNEL_PARTICIPANT_UPDATED,
            error,
            {
              participantId: event.participant.identity,
              channelUniqueName: event.participant.conversation.uniqueName,
            } as ChatTraceData,
          );
        });
      }
    });
  }

  private async actualizeMessagesReadStatus(participantLastReadMessageIndex: number): Promise<void> {
    if (this.loadedOriginalMessages.length !== this.convertedMessages.length) {
      throw new Error(
        `Active conversation has ${this.loadedOriginalMessages.length} original messages unequal to ` +
          `${this.convertedMessages.toString()} converted messages`,
      );
    }

    let earliestUnreadMessageIndex = -1;
    for (let i: number = this.convertedMessages.length - 1; i >= 0; i--) {
      if (this.convertedMessages[i].isRead) {
        break;
      }
      earliestUnreadMessageIndex = i;
    }

    const originalMessagesToActualize: Message[] = [];
    for (let i: number = earliestUnreadMessageIndex; i < this.convertedMessages.length; i++) {
      if (this.loadedOriginalMessages[i].index > participantLastReadMessageIndex) {
        break;
      }
      originalMessagesToActualize.push(this.loadedOriginalMessages[i]);
    }

    const stayingConvertedMessages: ChatMessage[] = this.convertedMessages.splice(0, earliestUnreadMessageIndex);
    const actualizedConvertedMessages: ChatMessage[] = await Promise.all(
      originalMessagesToActualize.map((m: Message) => this.convertTwilioMessage(m)),
    );
    this.convertedMessages = stayingConvertedMessages.concat(actualizedConvertedMessages);
    this.emitMessagesChanged();
  }

  private subscribeToUserEvents(): void {
    this.otherSubscriptions$.push(
      this.twilioUsersService.currentUserInfoUpdated.subscribe((userInfo: UserInfo) =>
        this.updateUserInfoInMessages(userInfo),
      ),
    );

    this.otherSubscriptions$.push(
      this.twilioUsersService.otherUserInfoUpdated.subscribe((userInfo: UserInfo) => {
        this.updateUserInfoInMessages(userInfo);

        // If the updated user is participating in this conversation, trigger details reload, i.ex. rebuild of a title
        if (
          this.activeChatConversation &&
          this.activeChatConversation.participatingUsers.some((u: UserInfo) => u.userId === userInfo.userId)
        ) {
          this.activeChannelDetailsChanged$.next();
        }
      }),
    );
  }

  private updateUserInfoInMessages(userInfo: UserInfo): void {
    let messagesUpdated = false;
    this.convertedMessages = this.convertedMessages.map((message: ChatMessage) => {
      if (message.sender.userId === userInfo.userId && !equalUserInfos(message.sender, userInfo)) {
        messagesUpdated = true;
        return { ...message, sender: userInfo };
      }
      return message;
    });

    if (messagesUpdated) {
      this.emitMessagesChanged();
    }
  }

  private subscribeToTypingEventsInChannel(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('typingStarted', (member: Participant) => {
      this.emitTypingChanges(member, true).catch((error: unknown) => {
        this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.ACTIVE_CHANNEL_TYPING_STARTED, error, {
          participantId: member.identity,
          channelUniqueName: member.conversation.uniqueName,
        } as ChatTraceData);
      });
    });

    twilioClient.on('typingEnded', (member: Participant) => {
      this.emitTypingChanges(member, false).catch((error: unknown) => {
        this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.ACTIVE_CHANNEL_TYPING_ENDED, error, {
          participantId: member.identity,
          channelUniqueName: member.conversation.uniqueName,
        } as ChatTraceData);
      });
    });
  }

  private async emitTypingChanges(participant: Participant, isTyping: boolean): Promise<void> {
    if (
      this.activeTwilioChannel &&
      this.activeTwilioChannel.uniqueName === participant.conversation.uniqueName &&
      participant.identity !== this.sessionService.userId
    ) {
      const user: User = await participant.getUser();
      this.typingActivity$.next({
        typingName: user.friendlyName,
        isTyping,
      } as ChatTypingIndicator);
    }
  }

  private clearActiveChannelData(): void {
    this.activeTwilioChannel = null;
    this.activeTwilioChannelParticipants = null;
    this.activeChatConversation = null;
    this.messagePaginatorPromise = null;
    this.loadedOriginalMessages = [];
    this.convertedMessages = [];
  }

  private async fetchActiveChannelMessages(): Promise<void> {
    if (this.messagePaginatorPromise === null) {
      return;
    }
    this.loadingMessages$.next(true);

    await this.readMessagesPage();
  }

  private async readMessagesPage(): Promise<void> {
    if (this.messagePaginatorPromise === null) {
      throw new Error('Message paginator promise missing');
    }

    // Obtain page
    const messagePaginator: Paginator<Message> = await this.messagePaginatorPromise;

    // Because of 'hidden', we might be in a need to scroll until the first non-hidden message is seen or scrolling ends
    let newFilteredTwilioMessages: Message[] = this.filterHiddenMessages(messagePaginator.items);
    if (newFilteredTwilioMessages.length === 0) {
      // All messages are hidden. Try next page
      if (messagePaginator.hasPrevPage) {
        // Recursive handler for the next page
        this.messagePaginatorPromise = messagePaginator.prevPage();
        await this.readMessagesPage();
        return;
      } else {
        // Reached end of messages list, stop loading
        this.messagePaginatorPromise = null;
        this.emitMessagesChanged();
        this.loadingMessages$.next(false);
        return;
      }
    }

    // Prepare to the next portion
    if (messagePaginator.hasPrevPage) {
      // There is more page
      this.messagePaginatorPromise = messagePaginator.prevPage();
    } else {
      // The last page reached
      this.messagePaginatorPromise = null;
    }

    // Publish current portion after potential deduplication
    // (normally there should not be, but anything happens in the network...)
    newFilteredTwilioMessages = newFilteredTwilioMessages.filter(
      (m: Message) => !this.loadedOriginalMessages.some((lm: Message) => lm.sid === m.sid),
    );

    const newConvertedMessages: ChatMessage[] = await Promise.all(
      newFilteredTwilioMessages.map((m: Message) => this.convertTwilioMessage(m)),
    );

    this.loadedOriginalMessages = newFilteredTwilioMessages.concat(this.loadedOriginalMessages);
    this.convertedMessages = newConvertedMessages.concat(this.convertedMessages);

    this.emitMessagesChanged();
    this.loadingMessages$.next(false);
  }

  private emitMessagesChanged(): void {
    this.activeChannelMessagesChanged$.next(this.convertedMessages);
  }

  private async convertTwilioMessage(twilioMessage: Message): Promise<ChatMessage> {
    const userAttributes: TwilioUserAttributes = this.twilioUsersService.currentUserAttributes;
    const blockedUserIds: string[] = userAttributes.blocked || [];

    const messageSender: UserInfo = await getTwilioMessageAuthorUser(
      this.twilioClientAccessService.getTwilioClient(),
      twilioMessage,
      this.activeChatConversation ? this.activeChatConversation.participatingUsers : [],
      blockedUserIds,
    );
    return TwilioMessagesAdapter.adaptTwilioMessageToCustomMessage(
      twilioMessage,
      this.activeTwilioChannelParticipants ? this.activeTwilioChannelParticipants : [],
      messageSender,
    );
  }

  private filterHiddenMessages(messages: Message[]): Message[] {
    const currentUserId: string = this.sessionService.userId;

    return messages.filter((message: Message) => {
      const attributes: TwilioMessageAttributes = twilioMessageAttributes(message);
      if (!attributes.hidden) {
        return true;
      }

      return !attributes.hidden.some((userId: string) => userId === currentUserId);
    });
  }
}
